import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Slide,
  InputAdornment,
  FormControlLabel, 
  Checkbox, 
} from "@mui/material";
import { Edit, Delete, Search } from "@mui/icons-material";
import AdminSidebar from "./AdminSidebar";

// Import BASE_URL from your backend configuration
import { BASE_URL } from "../backend/backend.js"; 

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    username: "",
    password: "", 
    enabled: true, 
    email: "",
    mobileNumber: "", 
  });

  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = () => {
    // Replaced hardcoded URL with BASE_URL
    axios
      .get(`${BASE_URL}/admin/getAllUser`, { withCredentials: true })
      .then((response) => {
        const transformedUsers = response.data.map((user) => ({
          userId: user.id,
          username: user.username,
          email: user.email || "-",
          role: user.roles.length > 0 ? user.roles[0].name : "N/A",
          status: user.enabled ? "Active" : "Inactive",
          enabled: user.enabled, 
          mobileNumber: user.mobileNumber || "-", 
        }));
        setUsers(transformedUsers);
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
      });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleUpdateUser = () => {
    if (editId !== null) {
      // Replaced hardcoded URL with BASE_URL
      const updateApiUrl = `${BASE_URL}/user/updateUser/${editId}`;
      const requestBody = {
        username: formData.username,
        password: formData.password,
        enabled: formData.enabled,
        email: formData.email,
        mobileNumber: formData.mobileNumber,
      };

      axios
        .put(updateApiUrl, requestBody, { withCredentials: true })
        .then((response) => {
          console.log("User updated successfully:", response.data);
          fetchUsers(); 
          setEditId(null);
          setFormData({
            username: "",
            password: "",
            enabled: true,
            email: "",
            mobileNumber: "",
          });
        })
        .catch((error) => {
          console.error("Error updating user:", error);
        });
    }
  };

  const handleEdit = (user) => {
    setFormData({
      username: user.username,
      password: "", 
      enabled: user.enabled,
      email: user.email === "-" ? "" : user.email,
      mobileNumber: user.mobileNumber === "-" ? "" : user.mobileNumber,
    });
    setEditId(user.userId);
  };

  const handleDelete = (id) => {
    const updatedUsers = users.filter((user) => user.userId !== id);
    setUsers(updatedUsers);
    if (editId === id) {
      setEditId(null);
      setFormData({
        username: "",
        password: "",
        enabled: true,
        email: "",
        mobileNumber: "",
      });
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AdminSidebar />

      <Box sx={{ flexGrow: 1, padding: 4 }}>
        <Typography variant="h4" sx={{ textAlign: "center", mb: 4 }}>
          User Management
        </Typography>

        <Grid container spacing={4}>
          {/* Form Section */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {editId ? "Edit User" : "Add New User"}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    fullWidth
                    type="password"
                    helperText={editId ? "Leave blank to keep current password" : ""}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Mobile Number"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                {editId && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Role"
                        name="role"
                        value={
                          users.find((user) => user.userId === editId)?.role ||
                          "N/A"
                        }
                        InputProps={{
                          readOnly: true,
                        }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Status"
                        name="status"
                        value={
                          users.find((user) => user.userId === editId)?.status ||
                          "Inactive"
                        }
                        InputProps={{
                          readOnly: true,
                        }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel 
                        control={
                          <Checkbox 
                            name="enabled"
                            checked={formData.enabled}
                            onChange={handleChange}
                          />
                        }
                        label="Enabled"
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color={editId ? "warning" : "primary"}
                    onClick={editId ? handleUpdateUser : () => {
                      console.log("Add user functionality needs to be implemented");
                    }}
                    fullWidth
                  >
                    {editId ? "Update User" : "Add User"}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Table Section */}
          <Grid item xs={12} md={8}>
            <Slide direction="down" in={true} mountOnEnter unmountOnExit>
              <Box mb={2}>
                <TextField
                  label="Search by Username or Email"
                  variant="outlined"
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Slide>

            <Paper>
              <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell>User ID</TableCell>
                      <TableCell>Username</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.userId}>
                        <TableCell>{user.userId}</TableCell>
                        <TableCell>{user.username}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.role}</TableCell>
                        <TableCell>{user.status}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton
                              color="primary"
                              onClick={() => handleEdit(user)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(user.userId)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No users found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default UserManager;